/**
 * Wraps an async operation so concurrent callers share one in-flight promise
 * instead of stacking duplicate operations (e.g. several requests failing with
 * the same status must trigger a single token refresh). Once the operation
 * settles, the next call starts a fresh one.
 */
export const createSingleFlight = <T>(
  operation: () => Promise<T>
): (() => Promise<T>) => {
  let inFlight: Promise<T> | null = null;

  return () => {
    if (!inFlight) {
      inFlight = operation().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };
};

/**
 * Like {@link createSingleFlight}, but after an operation settles further calls
 * resolve to `fallback` until `cooldownMs` has passed, instead of repeating it.
 */
export const createCooldownSingleFlight = <T>({
  operation,
  cooldownMs,
  fallback
}: {
  operation: () => Promise<T>;
  cooldownMs: number;
  fallback: T;
}): (() => Promise<T>) => {
  let lastSettledAt: number | null = null;
  const singleFlight = createSingleFlight(() =>
    operation().finally(() => {
      lastSettledAt = Date.now();
    })
  );
  return () =>
    lastSettledAt !== null && Date.now() - lastSettledAt < cooldownMs
      ? Promise.resolve(fallback)
      : singleFlight();
};
